package com.flowyride.core.designsystem.component

import androidx.compose.foundation.interaction.FocusInteraction
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.relocation.BringIntoViewRequester
import androidx.compose.foundation.relocation.bringIntoViewRequester
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.ui.ExperimentalComposeUiApi
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.unit.dp
import androidx.compose.ui.viewinterop.HtmlElementView
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.w3c.dom.HTMLElement
import org.w3c.dom.asList
import org.w3c.dom.events.Event
import org.w3c.dom.events.KeyboardEvent

private const val FIELD_ATTRIBUTE = "data-flowy-field"

/**
 * Material frame (label, border, icons, supporting text) drawn by Compose around a transparent native
 * `<input>`/`<textarea>`: mobile Safari opens its keyboard on it, password managers see and fill it.
 */
@OptIn(ExperimentalComposeUiApi::class)
@Composable
actual fun FlowyTextField(
    value: String,
    onValueChange: (String) -> Unit,
    label: String,
    modifier: Modifier,
    kind: FlowyTextFieldKind,
    supportingText: String?,
    passwordVisible: Boolean,
    imeAction: ImeAction,
    onImeAction: () -> Unit,
    trailingIcon: @Composable (() -> Unit)?,
) {
    val interactionSource = remember { MutableInteractionSource() }
    // Read by long-lived DOM listeners and by `update`.
    val currentValue by rememberUpdatedState(value)
    val currentOnValueChange by rememberUpdatedState(onValueChange)
    val currentOnImeAction by rememberUpdatedState(onImeAction)
    val currentImeAction by rememberUpdatedState(imeAction)
    val currentPasswordVisible by rememberUpdatedState(passwordVisible)
    val textColor by rememberUpdatedState(MaterialTheme.colorScheme.onSurface)
    val caretColor by rememberUpdatedState(MaterialTheme.colorScheme.primary)
    val multiline = kind == FlowyTextFieldKind.Message

    // Compose does not know the native field has focus, so it would not scroll it into view. In-app browsers
    // (Messenger, Instagram…) shrink the page when the keyboard opens: a field low in the form ended up hidden
    // under the keyboard, the rider typing blind. Scroll it back into view on focus and on every resize.
    val scope = rememberCoroutineScope()
    val revealer = remember { Revealer(scope) }
    DisposableEffect(revealer) {
        val onResize: (Event) -> Unit = { if (document.activeElement === revealer.field) revealer.revealSoon() }
        window.addEventListener("resize", onResize)
        onDispose { window.removeEventListener("resize", onResize) }
    }

    Box(modifier.bringIntoViewRequester(revealer.requester)) {
        OutlinedTextFieldDefaults.DecorationBox(
            value = value,
            innerTextField = {
                HtmlElementView(
                    factory = {
                        createField(kind).apply {
                            revealer.field = this
                            var focus: FocusInteraction.Focus? = null
                            addEventListener("input", { currentOnValueChange(asDynamic().value as String) })
                            // Some password managers only fire "change" after filling.
                            addEventListener("change", { currentOnValueChange(asDynamic().value as String) })
                            addEventListener("focus", {
                                focus = FocusInteraction.Focus().also(interactionSource::tryEmit)
                                revealer.revealSoon()
                            })
                            addEventListener("blur", {
                                focus?.let { interactionSource.tryEmit(FocusInteraction.Unfocus(it)) }
                                focus = null
                                revealer.onBlur()
                            })
                            addEventListener("keydown", { event ->
                                event as KeyboardEvent
                                if (event.key != "Enter" || multiline) return@addEventListener
                                event.preventDefault()
                                if (currentImeAction == ImeAction.Next) focusNextField(this) else currentOnImeAction()
                            })
                        }
                    },
                    modifier = Modifier.fillMaxWidth().height(if (multiline) 96.dp else 56.dp),
                    update = { element ->
                        // Only when it differs: rewriting the value would move the caret to the end.
                        if (element.asDynamic().value != currentValue) element.asDynamic().value = currentValue
                        if (kind.isPassword) element.setAttribute("type", if (currentPasswordVisible) "text" else "password")
                        element.setAttribute("enterkeyhint", currentImeAction.enterKeyHint())
                        element.style.color = textColor.toCss()
                        element.style.setProperty("caret-color", caretColor.toCss())
                    },
                )
            },
            enabled = true,
            singleLine = !multiline,
            visualTransformation = VisualTransformation.None,
            interactionSource = interactionSource,
            label = { Text(label) },
            trailingIcon = trailingIcon,
            supportingText = supportingText?.let { { Text(it) } },
            // The native field fills the whole height, so a tap anywhere in the box lands on it.
            contentPadding = PaddingValues(horizontal = 16.dp),
        )
    }
}

/** Scrolls the field into view once the keyboard has opened and the page has been laid out again. */
private class Revealer(private val scope: CoroutineScope) {
    val requester = BringIntoViewRequester()
    var field: HTMLElement? = null
    private var job: Job? = null

    fun revealSoon() {
        job?.cancel()
        job = scope.launch {
            delay(300)
            requester.bringIntoView()
            if (restoreFocus) {
                restoreFocus = false
                field?.focus()
            }
        }
    }

    /**
     * Compose hides a native field lying entirely outside the visible area, which takes the focus away and closes
     * the keyboard: a field low in the form, covered by the keyboard that its own focus opened. Scroll it back, then
     * give the focus back.
     */
    fun onBlur() {
        if (field?.parentElement?.asDynamic()?.style?.visibility == "hidden") {
            restoreFocus = true
            revealSoon()
        }
    }

    private var restoreFocus = false
}

private fun createField(kind: FlowyTextFieldKind): HTMLElement {
    val element = document.createElement(if (kind == FlowyTextFieldKind.Message) "textarea" else "input") as HTMLElement
    val attributes = when (kind) {
        FlowyTextFieldKind.Username -> mapOf("type" to "text", "autocomplete" to "username", "name" to "username", "autocapitalize" to "none")
        FlowyTextFieldKind.Email -> mapOf("type" to "email", "autocomplete" to "email", "name" to "email", "inputmode" to "email")
        FlowyTextFieldKind.CurrentPassword -> mapOf("type" to "password", "autocomplete" to "current-password", "name" to "password")
        FlowyTextFieldKind.NewPassword -> mapOf("type" to "password", "autocomplete" to "new-password", "name" to "new-password")
        FlowyTextFieldKind.Text -> mapOf("type" to "text", "autocomplete" to "off")
        FlowyTextFieldKind.Message -> mapOf("autocomplete" to "off")
    }
    attributes.forEach { (name, value) -> element.setAttribute(name, value) }
    if (kind != FlowyTextFieldKind.Message && kind != FlowyTextFieldKind.Text) {
        element.setAttribute("autocorrect", "off")
        element.setAttribute("spellcheck", "false")
    }
    element.setAttribute(FIELD_ATTRIBUTE, "")
    element.setAttribute(
        "style",
        // 16px minimum: below that, mobile Safari zooms the page on focus.
        "width:100%;height:100%;box-sizing:border-box;margin:0;border:none;outline:none;background:transparent;" +
            "font:16px/24px Roboto,system-ui,-apple-system,sans-serif;resize:none;-webkit-appearance:none;appearance:none;" +
            (if (kind == FlowyTextFieldKind.Message) "padding:16px 0;" else "padding:0;"),
    )
    return element
}

/** Keyboard "Next": the following FlowyTextField in page order. */
private fun focusNextField(current: HTMLElement) {
    val fields = document.querySelectorAll("[$FIELD_ATTRIBUTE]").asList().filterIsInstance<HTMLElement>()
    val index = fields.indexOf(current)
    fields.getOrNull(index + 1)?.focus()
}

private fun ImeAction.enterKeyHint(): String = when (this) {
    ImeAction.Next -> "next"
    ImeAction.Done -> "done"
    ImeAction.Go -> "go"
    ImeAction.Search -> "search"
    ImeAction.Send -> "send"
    else -> "enter"
}

private fun Color.toCss(): String =
    "rgba(${(red * 255).toInt()},${(green * 255).toInt()},${(blue * 255).toInt()},$alpha)"
